const STORAGE_KEY = 'rsg_guest_marketplace_cart';

export type GuestMarketplaceCartLine = {
  marketPlaceSpecimenId: number;
  quantity: number;
  sellerAccountId: number;
  specimenId: number;
  embryoStrawId?: number;
};

const readLines = (): GuestMarketplaceCartLine[] => {
  try {
    const raw = globalThis.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (line): line is GuestMarketplaceCartLine =>
        line != null &&
        typeof line === 'object' &&
        typeof (line as GuestMarketplaceCartLine).marketPlaceSpecimenId === 'number' &&
        typeof (line as GuestMarketplaceCartLine).quantity === 'number',
    );
  } catch {
    return [];
  }
};

const writeLines = (lines: GuestMarketplaceCartLine[]): void => {
  globalThis.localStorage.setItem(STORAGE_KEY, JSON.stringify(lines));
};

export const getGuestMarketplaceCartLines = (): GuestMarketplaceCartLine[] => readLines();

export const getGuestMarketplaceCartTotalQuantity = (): number =>
  readLines().reduce((sum, line) => sum + (line.quantity ?? 0), 0);

export const addOrUpdateGuestMarketplaceCartLine = (line: GuestMarketplaceCartLine): void => {
  const lines = readLines();
  const index = lines.findIndex(existing => existing.marketPlaceSpecimenId === line.marketPlaceSpecimenId);
  if (index >= 0) {
    lines[index] = {
      ...lines[index],
      ...line,
      quantity: lines[index].quantity + line.quantity,
    };
  } else {
    lines.push(line);
  }
  writeLines(lines);
};

export const updateGuestMarketplaceCartLineQuantity = (marketPlaceSpecimenId: number, quantity: number): void => {
  const lines = readLines();
  const index = lines.findIndex(existing => existing.marketPlaceSpecimenId === marketPlaceSpecimenId);
  if (index < 0) return;
  lines[index] = { ...lines[index], quantity: Math.max(1, quantity) };
  writeLines(lines);
};

export const removeGuestMarketplaceCartLine = (marketPlaceSpecimenId: number): void => {
  const lines = readLines().filter(existing => existing.marketPlaceSpecimenId !== marketPlaceSpecimenId);
  writeLines(lines);
};

export const clearGuestMarketplaceCart = (): void => {
  globalThis.localStorage.removeItem(STORAGE_KEY);
};

export const syncGuestMarketplaceCartToAccount = async (
  accountId: number,
  addToMarketplaceCart: (payload: {
    accountId: number;
    quantity: number;
    sellerAccountId: number;
    specimenId: number;
    marketPlaceSpecimenId: number;
    embryoStrawId?: number;
  }) => Promise<unknown>,
): Promise<void> => {
  const lines = readLines();
  if (lines.length === 0) return;

  const failedLines: GuestMarketplaceCartLine[] = [];

  for (const line of lines) {
    try {
      const payload = {
        accountId,
        quantity: line.quantity,
        sellerAccountId: line.sellerAccountId,
        specimenId: line.specimenId,
        marketPlaceSpecimenId: line.marketPlaceSpecimenId,
      };
      if (line.embryoStrawId !== undefined) {
        Object.assign(payload, { embryoStrawId: line.embryoStrawId });
      }
      await addToMarketplaceCart(payload);
    } catch {
      failedLines.push(line);
    }
  }

  if (failedLines.length > 0) {
    writeLines(failedLines);
    throw new Error('Failed to sync all guest marketplace cart lines.');
  }

  clearGuestMarketplaceCart();
};
